import axios from 'axios';

import apiClient from './client';
import { computeFingerprint } from '../lib/fingerprint';
import {
  clearTrustToken,
  getTrustToken,
  saveTrustToken,
} from '../lib/trust-device-store';
import { useAuthStore } from '../store/auth';

/**
 * Auth API wrappers.
 *
 * /auth/login and /auth/verify-totp use a separate, un-intercepted axios
 * instance — these endpoints DON'T have an access token to send (we're
 * trying to acquire one), and a 401 here should surface to the user, not
 * trigger the refresh dance.
 *
 * Trust-device flow (per docs/PWA_BRIEF.md §9):
 *   - On every login attempt, send any locally-stored trust token + the
 *     UA fingerprint as headers. Backend will skip TOTP if both match.
 *   - After a TOTP success (i.e. there was no valid trust token), call
 *     /auth/trust-device to mint a fresh 30-day token, stash it.
 */

const authClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

export async function login({ email, password }) {
  const trustRow = await getTrustToken();
  const headers = {};
  if (trustRow) {
    headers['X-Trust-Token'] = trustRow.token;
    headers['X-User-Agent-Fingerprint'] = trustRow.fingerprint;
  }

  const response = await authClient.post(
    '/auth/login',
    { email, password },
    { headers },
  );
  return response.data;
}

export async function verifyTotp({ tempToken, code }) {
  const response = await authClient.post('/auth/verify-totp', {
    temp_token: tempToken,
    code,
  });
  return response.data;
}

/**
 * Mint a 30-day trust token bound to this device. Call after a successful
 * login that required TOTP, so the next login can skip TOTP.
 */
export async function createTrustDevice() {
  const fingerprint = await computeFingerprint();
  const response = await apiClient.post('/auth/trust-device', {
    user_agent_fingerprint: fingerprint,
  });
  await saveTrustToken(
    response.data.trust_token,
    fingerprint,
    response.data.expires_at,
  );
  return response.data;
}

/**
 * Revoke this device's trust token (best-effort) and clear it locally.
 */
export async function revokeTrustDevice() {
  const trustRow = await getTrustToken();
  if (!trustRow) return;

  try {
    await apiClient.delete('/auth/trust-device', {
      headers: { 'X-Trust-Token': trustRow.token },
    });
  } catch (err) {
    // Server-side revocation failed (network, expired, etc.). We still wipe
    // locally — worst case the row stays orphan in trusted_devices on the
    // server until expiry, which is harmless.
    console.warn('Failed to revoke trust-device server-side:', err);
  } finally {
    await clearTrustToken();
  }
}

/**
 * Full sign-out: revoke the trust-device row + clear all in-memory tokens.
 */
export async function logout() {
  await revokeTrustDevice();
  useAuthStore.getState().clear();
}
